import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import styles from './index.module.scss';

/* -------------------------------------------------------------------------- */
/*                                   Types                                    */
/* -------------------------------------------------------------------------- */

type ScrollAreaOrientation = 'vertical' | 'horizontal';

export interface ScrollAreaProps extends React.HTMLAttributes<HTMLDivElement> {
  /** 自定义类名 */
  className?: string;
  /** 视口区域自定义类名 */
  viewportClassName?: string;
  /** 滚动条方向，默认 vertical */
  orientation?: ScrollAreaOrientation;
  /** 是否同时显示水平滚动条 */
  showHorizontalScrollbar?: boolean;
  /** 滚动条自动隐藏延迟(ms)，0 表示不隐藏 */
  hideDelay?: number;
  /** 滚动条尺寸: thin(6px) | default(8px) | thick(10px) */
  scrollbarSize?: 'thin' | 'default' | 'thick';
}

export interface ScrollBarProps {
  orientation?: ScrollAreaOrientation;
  /** 滚动条尺寸 */
  size?: 'thin' | 'default' | 'thick';
  /** 自动隐藏延迟(ms) */
  hideDelay?: number;
  /** 关联的视口元素 */
  viewportRef: React.RefObject<HTMLDivElement>;
}

/* -------------------------------------------------------------------------- */
/*                                 ScrollBar                                  */
/* -------------------------------------------------------------------------- */

export function ScrollBar({
  orientation = 'vertical',
  size = 'default',
  hideDelay = 1000,
  viewportRef,
}: ScrollBarProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const [thumbSize, setThumbSize] = useState(0);
  const [thumbOffset, setThumbOffset] = useState(0);
  const [visible, setVisible] = useState(hideDelay === 0);
  const [isDragging, setIsDragging] = useState(false);
  const hideTimerRef = useRef<number>(0);
  const dragStartRef = useRef({ startPos: 0, startScroll: 0 });

  const isVertical = orientation === 'vertical';

  useEffect(() => {
    if (hideDelay === 0) {
      setVisible(true);
    }
  }, [hideDelay]);

  // 计算 thumb 尺寸和位置
  const updateThumb = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const {
      scrollHeight, scrollWidth, clientHeight, clientWidth, scrollTop, scrollLeft,
    } = viewport;

    const contentSize = isVertical ? scrollHeight : scrollWidth;
    const viewportSize = isVertical ? clientHeight : clientWidth;
    const scrollPos = isVertical ? scrollTop : scrollLeft;

    if (contentSize <= viewportSize) {
      setThumbSize(0);
      return;
    }

    const ratio = viewportSize / contentSize;
    const newThumbSize = Math.max(ratio * viewportSize, 20); // 最小 20px
    const maxOffset = viewportSize - newThumbSize;
    const scrollRatio = scrollPos / (contentSize - viewportSize);
    const newOffset = scrollRatio * maxOffset;

    setThumbSize(newThumbSize);
    setThumbOffset(newOffset);
  }, [isVertical, viewportRef]);

  // 显示/隐藏逻辑
  const showScrollbar = useCallback(() => {
    setVisible(true);
    clearTimeout(hideTimerRef.current);
    if (hideDelay > 0) {
      hideTimerRef.current = window.setTimeout(() => {
        if (!isDragging) {
          setVisible(false);
        }
      }, hideDelay);
    }
  }, [hideDelay, isDragging]);

  // 监听视口滚动
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;

    const handleScroll = () => {
      updateThumb();
      showScrollbar();
    };

    viewport.addEventListener('scroll', handleScroll, { passive: true });
    return () => viewport.removeEventListener('scroll', handleScroll);
  }, [viewportRef, updateThumb, showScrollbar]);

  // 监听视口尺寸变化
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;

    const resizeObserver = new ResizeObserver(() => {
      updateThumb();
    });
    resizeObserver.observe(viewport);

    // 同时观察内容变化
    const mutationObserver = new MutationObserver(() => {
      updateThumb();
    });
    mutationObserver.observe(viewport, { childList: true, subtree: true });

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [viewportRef, updateThumb]);

  // 初始化
  useEffect(() => {
    updateThumb();
  }, [updateThumb]);

  // 拖拽 thumb
  const handleThumbPointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const viewport = viewportRef.current;
    if (!viewport) return;

    setIsDragging(true);
    const pos = isVertical ? e.clientY : e.clientX;
    const scroll = isVertical ? viewport.scrollTop : viewport.scrollLeft;
    dragStartRef.current = { startPos: pos, startScroll: scroll };

    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [isVertical, viewportRef]);

  const handleThumbPointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging) return;
    const viewport = viewportRef.current;
    if (!viewport) return;

    const {
      scrollHeight, scrollWidth, clientHeight, clientWidth,
    } = viewport;
    const contentSize = isVertical ? scrollHeight : scrollWidth;
    const viewportSize = isVertical ? clientHeight : clientWidth;

    const pos = isVertical ? e.clientY : e.clientX;
    const delta = pos - dragStartRef.current.startPos;
    const ratio = contentSize / viewportSize;

    if (isVertical) {
      viewport.scrollTop = dragStartRef.current.startScroll + delta * ratio;
    } else {
      viewport.scrollLeft = dragStartRef.current.startScroll + delta * ratio;
    }
  }, [isDragging, isVertical, viewportRef]);

  const handleThumbPointerUp = useCallback((e: React.PointerEvent) => {
    setIsDragging(false);
    (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    // 延迟隐藏
    if (hideDelay > 0) {
      hideTimerRef.current = window.setTimeout(() => {
        setVisible(false);
      }, hideDelay);
    }
  }, [hideDelay]);

  // 点击 track 跳转
  const handleTrackClick = useCallback((e: React.MouseEvent) => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    if (!viewport || !track) return;
    if (e.target === thumbRef.current) return;

    const trackRect = track.getBoundingClientRect();
    const clickPos = isVertical
      ? e.clientY - trackRect.top
      : e.clientX - trackRect.left;
    const trackSize = isVertical ? trackRect.height : trackRect.width;
    const ratio = clickPos / trackSize;

    const {
      scrollHeight, scrollWidth, clientHeight, clientWidth,
    } = viewport;
    const contentSize = isVertical ? scrollHeight : scrollWidth;
    const viewportSize = isVertical ? clientHeight : clientWidth;
    const maxScroll = contentSize - viewportSize;

    if (isVertical) {
      viewport.scrollTop = ratio * maxScroll;
    } else {
      viewport.scrollLeft = ratio * maxScroll;
    }
  }, [isVertical, viewportRef]);

  if (thumbSize === 0) return null;

  const thumbStyle: React.CSSProperties = isVertical
    ? { height: `${thumbSize}px`, transform: `translateY(${thumbOffset}px)` }
    : { width: `${thumbSize}px`, transform: `translateX(${thumbOffset}px)` };

  return (
    <div
      ref={trackRef}
      className={classNames(
        styles.scrollbar,
        styles[orientation],
        styles[`size-${size}`],
        {
          [styles.visible]: visible || isDragging,
          [styles.dragging]: isDragging,
        },
      )}
      onClick={handleTrackClick}
    >
      <div
        ref={thumbRef}
        className={styles.thumb}
        style={thumbStyle}
        onPointerDown={handleThumbPointerDown}
        onPointerMove={handleThumbPointerMove}
        onPointerUp={handleThumbPointerUp}
      />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                                 ScrollArea                                 */
/* -------------------------------------------------------------------------- */

const ScrollArea = forwardRef<HTMLDivElement, ScrollAreaProps>(({
  className,
  viewportClassName,
  orientation = 'vertical',
  showHorizontalScrollbar = false,
  hideDelay = 1000,
  scrollbarSize = 'default',
  children,
  ...props
}, ref) => {
  const viewportRef = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={ref}
      className={classNames(styles.scrollArea, className)}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    >
      <div
        ref={viewportRef}
        className={classNames(styles.viewport, viewportClassName)}
      >
        {children}
      </div>

      {/* 垂直滚动条 */}
      {(orientation === 'vertical' || showHorizontalScrollbar) && (
        <ScrollBar
          orientation="vertical"
          size={scrollbarSize}
          hideDelay={hideDelay}
          viewportRef={viewportRef}
        />
      )}

      {/* 水平滚动条 */}
      {(orientation === 'horizontal' || showHorizontalScrollbar) && (
        <ScrollBar
          orientation="horizontal"
          size={scrollbarSize}
          hideDelay={hideDelay}
          viewportRef={viewportRef}
        />
      )}
    </div>
  );
});

ScrollArea.displayName = 'ScrollArea';

export default ScrollArea;
